import { Component, ReactNode } from 'react';
import { reportError } from '@/lib/errorReporter';
import { isChunkLoadError as sharedIsChunkLoadError, recoverFromChunkError } from '@/lib/chunkLoadError';
import { ErrorBoundaryFallback } from './ErrorBoundaryFallback';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  name?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorId: string;
}

function generateErrorId(): string {
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 8);
  return `err_${ts}_${rand}`;
}

// Chunk-load error patterns the main.tsx recovery wrapper looks for. Kept in
// sync with the regex in src/main.tsx (handleChunkError). When a route module
// fails to load — stale session pointing at a deploy-rotated chunk hash,
// mobile network drop, transient CDN edge 404 — React's lazy() rejects the
// import promise, and ErrorBoundary catches it BEFORE it can bubble to the
// window's unhandledrejection listener. That meant the recovery reload in
// main.tsx silently never fired for these cases (reported pattern:
// err_mphgrg8n_hlwjmd, Wesley, designer, Android Chrome 147, 2026-05-23).
// Detecting the same pattern here and reloading inside componentDidCatch
// closes that gap. Same sessionStorage flag guards against infinite reload
// loops when the chunk is genuinely missing.
// Shared matcher (src/lib/chunkLoadError) so this inner boundary recognizes the
// same Chrome/Firefox/Safari/stale-export phrasings as RootErrorBoundary and
// main.tsx — previously it matched only Chrome/Safari, so a Firefox or
// stale-binding chunk failure on the designer/dashboard (this boundary's routes,
// the primary paid surface) white-screened instead of self-healing.
function isChunkLoadError(error: Error | null): boolean {
  return !!error && sharedIsChunkLoadError(error);
}

// Recovery itself lives in src/lib/chunkLoadError so this boundary,
// RootErrorBoundary and main.tsx share one reload budget and one
// service-worker purge. This boundary used to keep a private one-shot flag and
// reload without purging, which meant a poisoned cache survived the reload and
// a budget already spent elsewhere was invisible here.

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, errorId: '' };

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Always mark hasError so React doesn't re-render the throwing child in a
    // loop. componentDidCatch handles chunk-load recovery by triggering a
    // reload before the fallback UI is meaningfully visible (no measurable
    // flash in practice — reload aborts pending paints).
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Chunk-load recovery: purge the service worker and reload before reporting
    // the error. The fresh index.html points at current chunk hashes and the
    // navigation retries successfully. Capped by the shared session budget so a
    // genuinely missing chunk still lands on the fallback UI instead of looping.
    if (isChunkLoadError(error) && recoverFromChunkError(error)) {
      return;
    }
    const errorId = generateErrorId();
    this.setState({ errorId });
    const componentName = this.props.name || 'unknown';
    console.error(`[ErrorBoundary:${componentName}]`, errorId, error, info.componentStack);
    reportError(error, {
      component: `ErrorBoundary:${componentName}`,
      componentStack: info.componentStack,
      errorId,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorId: '' });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <ErrorBoundaryFallback
          error={this.state.error}
          errorId={this.state.errorId}
          componentName={this.props.name || 'unknown'}
          onReset={this.handleReset}
        />
      );
    }
    return this.props.children;
  }
}
